import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useToast } from "../../components/Toast";
import { formatDate, formatNumber } from "../../lib/format";
import type { ApiError } from "../../api/client";
import { warehouseApi, type ReservableOrder, type Reservation, type StockRow } from "../../api/warehouse";

type T = ReturnType<typeof useTranslation>["t"];

/**
 * Šta se drži od jednog LOT-a, za koga i po kojoj porudžbini. Kupac i porudžbina tuđeg kupca se ne pišu („drugi
 * kupac“), ali se količina vidi, jer i ona nije slobodna. LOT na nalogu za otpremu koji još nije otišao stoji ovde
 * sa brojem naloga; njega oslobađa otpremnica, ne dugme.
 */
export function LotHolds({
  red,
  lang,
  mozePorudzbine,
  onChanged,
}: {
  red: StockRow;
  lang?: string;
  /** Broj porudžbine vodi na porudžbinu samo onome ko sme da je otvori. */
  mozePorudzbine: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [radi, setRadi] = useState<string | null>(null);

  async function otkazi(r: Reservation) {
    if (!r.id) return;
    setRadi(r.id);
    try {
      await warehouseApi.cancelReservation(r.id);
      toast.success(t("warehouse.reservationCancelled", "Rezervacija je otkazana."));
      onChanged();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(null);
    }
  }

  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {red.reservations.map((r, i) => (
        <li key={r.id ?? `${r.dispatchOrderId}-${i}`} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <b style={{ whiteSpace: "nowrap" }}>
            {formatNumber(r.quantity, lang, { maximumFractionDigits: 3 })} {red.unit ?? ""}
          </b>
          {(r.shipped ?? 0) > 0 && (
            <span className="muted" style={{ whiteSpace: "nowrap" }}>
              {t("warehouse.shippedPart", "od {{reserved}}, otišlo {{shipped}}", {
                reserved: `${formatNumber(r.quantity + (r.shipped ?? 0), lang, { maximumFractionDigits: 3 })} ${red.unit ?? ""}`.trim(),
                shipped: `${formatNumber(r.shipped, lang, { maximumFractionDigits: 3 })} ${red.unit ?? ""}`.trim(),
              })}
            </span>
          )}
          <span>{r.companyName ?? t("warehouse.otherCustomer", "drugi kupac")}</span>
          {r.orderNumber &&
            (mozePorudzbine && r.orderId ? (
              <Link to={`/orders/${r.orderId}`}>{r.orderNumber}</Link>
            ) : (
              <span className="mono">{r.orderNumber}</span>
            ))}
          {r.kind === "DISPATCH_ORDER" && (
            <span className="tag info">
              {r.dispatchOrderNumber
                ? t("warehouse.onDispatch", "na nalogu {{number}}", { number: r.dispatchOrderNumber })
                : t("warehouse.onDispatchNoNumber", "na nalogu za otpremu")}
            </span>
          )}
          {r.kind === "RESERVATION" && (
            <span className="muted">
              {[r.createdBy, r.createdAt ? formatDate(r.createdAt, lang) : null].filter(Boolean).join(", ")}
            </span>
          )}
          {r.note && <span className="muted">· {r.note}</span>}
          {r.mayCancel && r.id && (
            <button type="button" className="btn ghost sm" disabled={radi === r.id} onClick={() => void otkazi(r)}>
              {t("warehouse.cancelReservation", "Otkaži")}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Rezervacija dela LOT-a za porudžbinu. Nude se porudžbine koje čekaju robu i imaju artikal ovog LOT-a, i to samo
 * kupaca kojima korisnik sme da drži robu; kupac je kupac porudžbine. Količina se predlaže kao manje od slobodnog i
 * naručenog, a server je ponovo proverava, jer je u međuvremenu neko drugi mogao uzeti deo.
 */
export function ReserveForm({
  red,
  lang,
  onDone,
  onCancel,
}: {
  red: StockRow;
  lang?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [upit, setUpit] = useState("");
  const [porudzbine, setPorudzbine] = useState<ReservableOrder[] | null>(null);
  const [izabrana, setIzabrana] = useState<ReservableOrder | null>(null);
  const [kolicina, setKolicina] = useState("");
  const [napomena, setNapomena] = useState("");
  const [radi, setRadi] = useState(false);
  const slobodno = Math.max(0, red.lotFree ?? 0);

  // Pretraga ide posle kratke pauze, da svaki pritisak tastera ne bude upit.
  useEffect(() => {
    if (!red.goodsLotId) return;
    let vazi = true;
    const h = window.setTimeout(() => {
      warehouseApi
        .reservableOrders(red.goodsLotId!, upit)
        .then((p) => vazi && setPorudzbine(p))
        .catch(() => vazi && setPorudzbine([]));
    }, 250);
    return () => {
      vazi = false;
      window.clearTimeout(h);
    };
  }, [red.goodsLotId, upit]);

  function izaberi(p: ReservableOrder) {
    setIzabrana(p);
    const predlog = p.quantity != null ? Math.min(slobodno, p.quantity) : slobodno;
    setKolicina(String(Math.round(predlog * 1000) / 1000));
  }

  async function sacuvaj() {
    if (!izabrana || !red.goodsLotId) return;
    setRadi(true);
    try {
      await warehouseApi.reserve(red.goodsLotId, izabrana.orderId, Number(kolicina), napomena.trim() || null);
      toast.success(
        t("warehouse.reservedOk", "Rezervisano {{qty}} za {{order}}", {
          qty: `${formatNumber(Number(kolicina), lang, { maximumFractionDigits: 3 })} ${red.unit ?? ""}`.trim(),
          order: izabrana.orderNumber,
        }),
      );
      onDone();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(false);
    }
  }

  const broj = Number(kolicina);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <strong>
        {t("warehouse.reserveTitle", "Rezervacija LOT-a {{lot}}, slobodno {{free}}", {
          lot: red.lot ?? "",
          free: `${formatNumber(slobodno, lang, { maximumFractionDigits: 3 })} ${red.unit ?? ""}`.trim(),
        })}
      </strong>
      <input
        className="input"
        value={upit}
        onChange={(e) => setUpit(e.target.value)}
        placeholder={t("warehouse.orderSearch", "broj porudžbine ili kupac")}
        aria-label={t("warehouse.orderSearch", "broj porudžbine ili kupac")}
      />
      {porudzbine === null ? (
        <span className="muted">{t("warehouse.loadingOrders", "Tražim porudžbine…")}</span>
      ) : porudzbine.length === 0 ? (
        <span className="muted">{t("warehouse.noOrders", "Nema porudžbine koja čeka ovaj artikal.")}</span>
      ) : (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {porudzbine.map((p) => (
            <button
              key={p.orderId}
              type="button"
              className={`btn sm${izabrana?.orderId === p.orderId ? " primary" : ""}`}
              aria-pressed={izabrana?.orderId === p.orderId}
              onClick={() => izaberi(p)}
            >
              {p.orderNumber} · {p.companyName ?? "—"}
              {p.quantity != null ? ` · ${formatNumber(p.quantity, lang, { maximumFractionDigits: 3 })} ${p.unit ?? ""}` : ""}
              {" · "}
              {statusPorudzbine(t, p.status)}
            </button>
          ))}
        </div>
      )}
      <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          className="input"
          type="number"
          min="0"
          step="any"
          style={{ width: 160 }}
          value={kolicina}
          onChange={(e) => setKolicina(e.target.value)}
          aria-label={t("warehouse.quantity", "Količina")}
          placeholder={t("warehouse.quantity", "Količina")}
        />
        <span className="muted">{red.unit}</span>
        <input
          className="input"
          style={{ minWidth: 220, flex: 1 }}
          value={napomena}
          onChange={(e) => setNapomena(e.target.value)}
          aria-label={t("warehouse.note", "Napomena")}
          placeholder={t("warehouse.note", "Napomena")}
        />
        <button type="button" className="btn sm" onClick={onCancel}>
          {t("common.cancel", "Odustani")}
        </button>
        <button
          type="button"
          className="btn sm primary"
          disabled={radi || !izabrana || !(broj > 0)}
          onClick={() => void sacuvaj()}
        >
          {t("warehouse.reserveSave", "Rezerviši")}
        </button>
      </div>
    </div>
  );
}

function statusPorudzbine(t: T, status: string): string {
  switch (status) {
    case "SUBMITTED":
      return t("orders.status.submitted", "Poslato");
    case "IN_REVIEW":
      return t("orders.status.inReview", "Na pregledu");
    case "APPROVED":
      return t("orders.status.approved", "Odobreno");
    case "SHIPPED":
      return t("orders.status.shipped", "Otpremljeno");
    default:
      return status;
  }
}
